import Cookies from 'js-cookie';
import sharedConfig from '../constants/sharedConfig';

/**
 * Determines the brand based on window.MULTIBRAND_SUPPORT or hostname patterns.
 * Priority: MULTIBRAND_SUPPORT > hostname patterns > development fallback
 */
export function getBrand(hostName: string): Brand | undefined {
  // 1. Check for brand returned by Java via window.MULTIBRAND_SUPPORT
  const explicitBrand = getBrandFromMultiBrandSupport();
  if (explicitBrand) {
    return explicitBrand;
  }

  // 2. Determine brand from hostname patterns
  const hostnameBrand = getBrandFromHostname(hostName);
  if (hostnameBrand) {
    return hostnameBrand;
  }

  // 3. Development environment fallback
  if (isDevelopmentEnvironment(hostName)) {
    return getDevelopmentBrand();
  }

  return undefined;
}

/**
 * Gets brand from window.MULTIBRAND_SUPPORT if available and supported
 */
function getBrandFromMultiBrandSupport(): Brand | undefined {
  if (typeof window === 'undefined' || !window.MULTIBRAND_SUPPORT) {
    return undefined;
  }

  const multiBrand = window.MULTIBRAND_SUPPORT.brand;
  if (!multiBrand) {
    return undefined;
  }

  const isSupported = sharedConfig.supportedBrands[multiBrand];
  return isSupported ? (multiBrand as Brand) : undefined;
}

/**
 * Determines brand based on hostname patterns
 */
function getBrandFromHostname(hostName: string): Brand | undefined {
  const { hotpads, zillow } = sharedConfig.brands;

  // Check for HotPads patterns (including testpads)
  if (hostName.includes(hotpads) || hostName.includes('testpads')) {
    return hotpads;
  }

  // Check for Zillow patterns
  if (hostName.includes(zillow)) {
    return zillow;
  }

  return undefined;
}

/**
 * Checks if we're in a development environment
 */
function isDevelopmentEnvironment(hostName: string): boolean {
  return hostName.includes('localhost') || process.env.NODE_ENV === 'development';
}

/**
 * Gets the brand for development environment based on dev_brand cookie
 */
function getDevelopmentBrand(): Brand {
  const { hotpads, zillow } = sharedConfig.brands;
  const devBrandCookie = Cookies.get('dev_brand');

  return devBrandCookie === zillow ? zillow : hotpads;
}
